// Learning Tracks mutations. All admin-gated; the server enforces the
// `admin` role on each write endpoint. Each hook patches/invalidates the
// shared QueryClient cache so the list and detail views update in lockstep
// without needing to thread state through props.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { trackLearningTrackCreated } from '../analytics/events';
import { getApiClient } from '../api-client';
import { unwrap, unwrapVoid } from '../api-error';
import {
  learningTracksKeys,
  type LearningTrackDetail,
  type LearningTrackSummary,
} from '../queries/learning-tracks';

// ---- Inputs ----

export type CreateLearningTrackInput =
  components['schemas']['CreateLearningTrackRequest'];

export type UpdateLearningTrackInput =
  components['schemas']['UpdateLearningTrackRequest'];

// ---- Track-level mutations ----

export function useCreateLearningTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: CreateLearningTrackInput,
    ): Promise<LearningTrackSummary> => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/admin/learning-tracks', {
          body: input,
        }),
      );
    },
    onSuccess: () => {
      // Tracks are created empty and resources attached afterwards, so the
      // count is 0 by construction at this point.
      trackLearningTrackCreated({ resource_count: 0 });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useUpdateLearningTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly id: string;
      readonly patch: UpdateLearningTrackInput;
    }): Promise<LearningTrackSummary> => {
      const client = getApiClient();
      return unwrap(
        await client.PATCH('/api/v1/admin/learning-tracks/{id}', {
          params: { path: { id: input.id } },
          body: input.patch,
        }),
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: learningTracksKeys.detail(vars.id).queryKey });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useDeleteLearningTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/admin/learning-tracks/{id}', {
          params: { path: { id } },
        }),
      );
      return id;
    },
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: learningTracksKeys.detail(id).queryKey });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useReorderLearningTracks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedTrackIds: readonly string[]): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/learning-tracks/reorder', {
          body: { orderedTrackIds: [...orderedTrackIds] },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

// ---- Item-level mutations ----

export function useAttachLearningTrackResource(trackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly resourceId: string;
      readonly sortOrder?: number;
    }): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/learning-tracks/{id}/resources', {
          params: { path: { id: trackId } },
          body: {
            resourceId: input.resourceId,
            ...(input.sortOrder !== undefined
              ? { sortOrder: input.sortOrder }
              : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: learningTracksKeys.detail(trackId).queryKey,
      });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useDetachLearningTrackResource(trackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE(
          '/api/v1/admin/learning-tracks/{id}/resources/{resourceId}',
          { params: { path: { id: trackId, resourceId } } },
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: learningTracksKeys.detail(trackId).queryKey,
      });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useReorderLearningTrackResources(trackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      orderedResourceIds: readonly string[],
    ): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST(
          '/api/v1/admin/learning-tracks/{id}/resources/reorder',
          {
            params: { path: { id: trackId } },
            body: { orderedResourceIds: [...orderedResourceIds] },
          },
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: learningTracksKeys.detail(trackId).queryKey,
      });
    },
  });
}

// Viewer-self progress: optimistic check/uncheck of one curriculum item.
// The detail query is the single source the page renders from, so the
// optimistic write patches it directly and rolls back on error.
export function useSetTrackItemProgress(trackId: string) {
  const qc = useQueryClient();
  const detailKey = learningTracksKeys.detail(trackId).queryKey;
  return useMutation<
    void,
    Error,
    { resourceId: string; completed: boolean },
    { previous: LearningTrackDetail | undefined }
  >({
    mutationFn: async ({ resourceId, completed }) => {
      const client = getApiClient();
      const path = {
        params: { path: { id: trackId, resourceId } },
      };
      unwrapVoid(
        completed
          ? await client.PUT(
              '/api/v1/learning-tracks/{id}/progress/{resourceId}',
              path,
            )
          : await client.DELETE(
              '/api/v1/learning-tracks/{id}/progress/{resourceId}',
              path,
            ),
      );
    },
    onMutate: async ({ resourceId, completed }) => {
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<LearningTrackDetail>(detailKey);
      if (previous) {
        const items = previous.items.map((item) =>
          item.resourceId === resourceId
            ? { ...item, viewerCompleted: completed }
            : item,
        );
        qc.setQueryData<LearningTrackDetail>(detailKey, {
          ...previous,
          items,
          viewerCompletedCount: items.filter((i) => i.viewerCompleted).length,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: detailKey });
    },
  });
}
